
import { useMemo } from 'react';
import { 
  transformProvidersToModelOptions, 
  getFallbackModelOptions,
  ModelOption
} from '@/utils/modelUtils';
import { LLMProvider, ModelObject } from '@/hooks/useLLMProviders';
import { useSuperAdminLLMProviders } from './useSuperAdminLLMProviders';

export const useAIModels = (enabled: boolean = true) => {
  const { providers, isLoading } = useSuperAdminLLMProviders(enabled);

  const normalizedProviders = useMemo<LLMProvider[]>(() => {
    if (!providers || providers.length === 0) {
      return [];
    }

    return providers.filter(provider => provider.status?.trim().toLowerCase() !== 'inactive').map(provider => {
      const rawModels = Array.isArray(provider.models) ? provider.models : [];
      const normalizedModels: ModelObject[] = rawModels.map((model) => {
        if (typeof model === 'string') {
          return {
            id: 0,
            name: model,
            display_name: model,
            status: provider.status
          };
        }

        return {
          ...model,
          display_name: model.display_name || model.name,
          status: model.status || provider.status
        };
      }) as ModelObject[];

      const defaultModel = provider.default_model;
      const defaultModelValue = typeof defaultModel === 'object' && defaultModel !== null
        ? defaultModel.name
        : typeof defaultModel === 'string'
          ? defaultModel
          : null;

      return {
        provider_name: provider.provider_name,
        models: normalizedModels,
        default_model: defaultModelValue
      };
    });
  }, [providers]);

  const allModelOptions = useMemo(() => {

    if (!normalizedProviders || normalizedProviders.length === 0) {
      console.log('useAIModels - No providers, using fallback');
      return getFallbackModelOptions();
    }
    
    const transformed = transformProvidersToModelOptions(normalizedProviders);
    return transformed;
  }, [providers, normalizedProviders]);

  const modelOptionsForDropdown = useMemo(() => {
    const options = allModelOptions.map(option => ({
      value: option.value,
      label: option.label,
      status: option.status
    }));
    return options;
  }, [allModelOptions]);

  const getModelByValue = (value: string): ModelOption | undefined => {
    return allModelOptions.find(option => option.value === value);
  };



  return {
    providers,
    allModelOptions,
    modelOptionsForDropdown,
    isLoading,
    getModelByValue
  };
};
